import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { useBinanceAccount } from "@/hooks/exchanges/useBinanceBalances";
import { useAccountInfoStore } from "@/lib/stores/accountInfoStore";
import {
  TradeAccount,
  useTradeAccountsStore,
} from "@/lib/stores/userTradeAccounts";
import { useUserStore } from "@/lib/stores/userStore";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useEffect, useState } from "react";
import { useBinanceBalances } from "@/hooks/exchanges/useBinanceBalances";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const SUPPORTED_EXCHANGES = [
  {
    id: "BINANCE",
    name: "BINANCE",
    icon: "fa-brands fa-bitcoin",
    enabled: true,
  },
  {
    id: "BYBIT",
    name: "BYBIT",
    icon: "fa-solid fa-chart-line",
    enabled: false,
  },
  { id: "OKX", name: "OKX", icon: "fa-solid fa-chart-simple", enabled: false },
  { id: "KUCOIN", name: "KUCOIN", icon: "fa-solid fa-coins", enabled: false },
] as const;

type SupportedExchange = (typeof SUPPORTED_EXCHANGES)[number]["id"];

interface AddAccountFormData {
  exchange: SupportedExchange;
  exchangeType: "SPOT" | "PERPETUAL";
  apiKey: string;
  apiSecret: string;
  label: string;
  trading: boolean;
  journal: boolean;
  testnet: boolean;
}

export function Accounts() {
  const {
    accounts,
    addAccount,
    removeAccount,
    updateAccount,
    syncWithFirebase,
  } = useTradeAccountsStore();
  const user = useUserStore((state) => state.user);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [accountToRemove, setAccountToRemove] = useState<TradeAccount | null>(
    null,
  );
  const [accountToEdit, setAccountToEdit] = useState<TradeAccount | null>(null);
  const [formData, setFormData] = useState<AddAccountFormData>({
    exchange: "BINANCE",
    exchangeType: "SPOT",
    apiKey: "",
    apiSecret: "",
    label: "",
    trading: false,
    journal: false,
    testnet: false,
  });

  const resetForm = () => {
    setFormData({
      exchange: "BINANCE",
      exchangeType: "SPOT",
      apiKey: "",
      apiSecret: "",
      label: "",
      trading: false,
      journal: false,
      testnet: false,
    });
    setAccountToEdit(null);
  };

  const handleRemoveAccount = async (account: TradeAccount) => {
    try {
      await removeAccount(account.id);
      setAccountToRemove(null);
    } catch (error) {
      console.error("Failed to remove account:", error);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (accountToEdit) {
        await updateAccount(accountToEdit.id, formData);
      } else {
        await addAccount(formData);
      }
      setIsDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error("Failed to save account:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  useEffect(() => {
    if (user?.uid) {
      syncWithFirebase(user.uid);
    }
  }, [user?.uid, syncWithFirebase]);

  return (
    <Card className="bg-background p-4 border-neutral-700 h-full">
      <CardContent className="p-0">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-sm font-medium">Trading Accounts</h3>
          <Dialog
            open={isDialogOpen}
            onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) {
                resetForm();
              }
            }}
          >
            <DialogTrigger asChild>
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  resetForm();
                  setIsDialogOpen(true);
                }}
              >
                <i className="fa-regular fa-plus mr-2" />
                Add Account
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>
                  {accountToEdit
                    ? "Edit Trading Account"
                    : "Add Trading Account"}
                </DialogTitle>
                <DialogDescription>
                  {accountToEdit
                    ? "Modify your existing trading account settings."
                    : "Add a new trading account to connect with your exchange."}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Exchange Settings</Label>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <Select
                        value={formData.exchange}
                        onValueChange={(value: SupportedExchange) =>
                          setFormData((prev) => ({ ...prev, exchange: value }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue>
                            {SUPPORTED_EXCHANGES.find(
                              (e) => e.id === formData.exchange,
                            )?.name || "Select an exchange"}
                          </SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          {SUPPORTED_EXCHANGES.map((exchange) => (
                            <SelectItem
                              key={exchange.id}
                              value={exchange.id}
                              disabled={!exchange.enabled}
                              className="flex items-center justify-between w-full"
                            >
                              <div className="flex items-center">
                                <i
                                  className={`${exchange.icon} text-muted-foreground pr-2`}
                                />
                                <span>{exchange.name}</span>
                              </div>
                              {!exchange.enabled && (
                                <span className="text-xs text-muted-foreground">
                                  Coming soon
                                </span>
                              )}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex-1">
                      <Select
                        value={formData.exchangeType}
                        onValueChange={(value: "SPOT" | "PERPETUAL") =>
                          setFormData((prev) => ({
                            ...prev,
                            exchangeType: value,
                          }))
                        }
                      >
                        <SelectTrigger>
                          <SelectValue>{formData.exchangeType}</SelectValue>
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="SPOT">SPOT</SelectItem>
                          <SelectItem value="PERPETUAL">PERPETUAL</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="label">Account Label</Label>
                  <Input
                    id="label"
                    name="label"
                    value={formData.label}
                    onChange={handleInputChange}
                    placeholder="Optional"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiKey">API Key</Label>
                  <Input
                    id="apiKey"
                    name="apiKey"
                    value={formData.apiKey}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="apiSecret">API Secret</Label>
                  <Input
                    id="apiSecret"
                    name="apiSecret"
                    type="password"
                    value={formData.apiSecret}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="trading"
                    checked={formData.trading}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        trading: Boolean(checked),
                      }))
                    }
                  />
                  <Label htmlFor="trading">Enable Trading</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="journal"
                    checked={formData.journal}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        journal: Boolean(checked),
                      }))
                    }
                  />
                  <Label htmlFor="journal">Enable Journal</Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="testnet"
                    checked={formData.testnet}
                    onCheckedChange={(checked) =>
                      setFormData((prev) => ({
                        ...prev,
                        testnet: Boolean(checked),
                      }))
                    }
                  />
                  <Label htmlFor="testnet">Enable Testnet</Label>
                </div>
                <Button type="submit" className="w-full">
                  {accountToEdit ? "Save Changes" : "Add Account"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
        <div className="space-y-3">
          {accounts.map((account) => (
            <div
              key={account.id}
              className="flex flex-col p-3 bg-muted rounded-lg space-y-2"
            >
              <div className="flex justify-between items-center">
                <div className="flex items-center space-x-4">
                  <div>
                    <p className="text-sm font-medium">
                      {account.label || account.exchange}
                    </p>
                    <div className="flex items-center space-x-2 text-xs text-muted-foreground">
                      <i
                        className={`${SUPPORTED_EXCHANGES.find((e) => e.id === account.exchange)?.icon}`}
                      />
                      <span>
                        {account.exchange} {account.exchangeType}
                      </span>
                    </div>
                  </div>
                  {account.exchange === "BINANCE" &&
                    account.exchangeType === "PERPETUAL" && (
                      <BinanceAccountInfo account={account} />
                    )}
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => {
                      setAccountToEdit(account);
                      setFormData({
                        exchange: account.exchange as SupportedExchange,
                        exchangeType: account.exchangeType,
                        apiKey: account.apiKey,
                        apiSecret: account.apiSecret,
                        label: account.label || "",
                        trading: account.trading,
                        journal: account.journal,
                        testnet: account.testnet,
                      });
                      setIsDialogOpen(true);
                    }}
                  >
                    <i className="fa-regular fa-pen-to-square" />
                  </Button>
                  <Dialog
                    open={accountToRemove?.id === account.id}
                    onOpenChange={(open) => !open && setAccountToRemove(null)}
                  >
                    <DialogTrigger asChild>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="hover:text-red-400"
                        onClick={() => setAccountToRemove(account)}
                      >
                        <i className="fa-regular fa-trash-can" />
                      </Button>
                    </DialogTrigger>
                    <DialogContent>
                      <DialogHeader>
                        <DialogTitle>Remove Trading Account</DialogTitle>
                        <DialogDescription>
                          This will permanently remove your trading account
                          connection.
                        </DialogDescription>
                      </DialogHeader>
                      <div className="space-y-4">
                        <p>
                          Are you sure you want to remove this trading account?
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {account.label || account.exchange}
                        </p>
                        <div className="flex justify-end space-x-2">
                          <Button
                            variant="outline"
                            onClick={() => setAccountToRemove(null)}
                          >
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => handleRemoveAccount(account)}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </div>
          ))}
          {accounts.length === 0 && (
            <p className="text-sm text-muted-foreground text-center py-4">
              No trading accounts added yet
            </p>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

function BinanceAccountInfo({ account }: { account: TradeAccount }) {
  // Use the hook to initialize account info in the store
  useBinanceAccount(account);

  // Get account info directly from the store
  const { getAccountInfo, isLoading, getError } = useAccountInfoStore();
  const accountInfo = account?.id ? getAccountInfo(account.id) : null;
  const loading = account?.id ? isLoading(account.id) : false;
  const error = account?.id ? getError(account.id) : null;

  if (error) {
    return <div className="text-sm text-red-500">{error}</div>;
  }

  if (!accountInfo) {
    return null;
  }

  const formatUSD = (value: string) => {
    const num = parseFloat(value);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(num);
  };

  const unrealizedPnL = parseFloat(accountInfo.totalUnrealizedProfit);
  const pnlColor =
    unrealizedPnL > 0
      ? "text-green-500"
      : unrealizedPnL < 0
        ? "text-red-500"
        : "text-muted-foreground";

  return (
    <div className="grid grid-cols-2 gap-x-8 text-sm min-w-[400px]">
      <div className="grid grid-cols-[80px_1fr] items-center">
        <span className="text-muted-foreground">Wallet:</span>
        <span className="font-medium">
          {formatUSD(accountInfo.totalWalletBalance)}
        </span>
      </div>
      <div className="grid grid-cols-[80px_1fr] items-center">
        <span className="text-muted-foreground">Margin:</span>
        <span className="font-medium">
          {formatUSD(accountInfo.totalMarginBalance)}
        </span>
      </div>
      <div className="grid grid-cols-[80px_1fr] items-center">
        <span className="text-muted-foreground">Available:</span>
        <span className="font-medium">
          {formatUSD(accountInfo.availableBalance)}
        </span>
      </div>
      <div className="grid grid-cols-[80px_1fr] items-center">
        <span className="text-muted-foreground">PnL:</span>
        <span className={`font-medium ${pnlColor}`}>
          {formatUSD(accountInfo.totalUnrealizedProfit)}
        </span>
      </div>
    </div>
  );
}
